const inflightByLockId = new Map<string, Promise<unknown>>();

/**
 * Prevents duplicate operations on the same matter folder.
 *
 * Specifically, this prevents the same folder from being created twice
 * when multiple code paths perform a get-or-create for the folder at the same time.
 * @param lockId
 * @param task
 * @returns
 */
export const withFolderLock = async <T>(
  lockId: string,
  task: () => Promise<T>,
): Promise<T> => {
  // Lock the matter folder across tabs and windows if API is available.
  // @see https://developer.mozilla.org/en-US/docs/Web/API/Web_Locks_API
  const hasWebLocks =
    typeof navigator !== 'undefined' && (navigator as any).locks?.request;
  if (hasWebLocks) {
    return (navigator as any).locks.request(
      `practist.io:lock:${lockId}`,
      { mode: 'exclusive' },
      task,
    );
  }

  // Fallback to inflight map (current tab only) if Web Locks API is not available.
  const existing = inflightByLockId.get(lockId) as Promise<T> | undefined;
  if (existing) return existing;

  const run = (async () => {
    try {
      return await task();
    } finally {
      inflightByLockId.delete(lockId);
    }
  })();

  inflightByLockId.set(lockId, run as Promise<unknown>);
  return run;
};
